import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/pt';
import { useState, useEffect } from 'react';
import axiosInstance, { API_ENDPOINTS, HOST_API } from '../../axios/axiosInstance';

dayjs.locale('pt');
dayjs.extend(utc);
dayjs.extend(timezone);

const tz = 'Europe/Luxembourg';
dayjs.tz.setDefault(tz);

export default function RHFDateTimePicker({ name, helperText, type, ...other }) {
  const { control, setValue } = useFormContext();
  const [businessHours, setBusinessHours] = useState([]);
  const [closedDates, setClosedDates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch business hours and closed dates from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [hoursRes, closedRes] = await Promise.all([
          axiosInstance.get(HOST_API + API_ENDPOINTS.businessHours.list),
          axiosInstance.get(HOST_API + API_ENDPOINTS.businessHours.closedDates)
        ]);
        setBusinessHours(hoursRes.data);
        setClosedDates(closedRes.data);
      } catch (error) {
        console.error('Error fetching business hours:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Traduções personalizadas para o DateTimePicker
  const localeText = {
    cancelButtonLabel: 'Cancelar',
    clearButtonLabel: 'Limpar',
    okButtonLabel: 'OK',
    todayButtonLabel: 'Hoje',
    datePickerToolbarTitle: 'Selecione a data',
    timePickerToolbarTitle: 'Selecione o horário',
    dateTimePickerToolbarTitle: 'Selecione a data e o horário',
    previousMonth: 'Mês anterior',
    nextMonth: 'Próximo mês',
    weekdays: ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'],
    weekdaysShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
  };

  // Parse time string "HH:mm" to hours and minutes
  const parseTime = (timeStr) => {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return { hours, minutes };
  };

  // Get business hours for a specific day of week
  const getHoursForDay = (dayOfWeek) => {
    return businessHours.find(bh => bh.dayOfWeek === dayOfWeek);
  };

  // Function to disable closed days and special closed dates
  const shouldDisableDate = (date) => {
    const dayOfWeek = date.day();
    const hoursForDay = getHoursForDay(dayOfWeek);

    // Disable if day is marked as closed in business hours
    if (hoursForDay && !hoursForDay.isOpen) {
      return true;
    }

    // Disable special closed dates
    const dateStr = date.format('YYYY-MM-DD');
    const isSpecialClosed = closedDates.some(cd => {
      const closedDate = dayjs(cd.date).format('YYYY-MM-DD');
      return closedDate === dateStr;
    });

    if (isSpecialClosed) {
      return true;
    }

    // Disable past dates
    if (date.isBefore(dayjs(), 'day')) {
      return true;
    }

    return false;
  };

  // Function to disable times outside business hours
  const shouldDisableTime = (timeValue, view) => {
    if (loading || businessHours.length === 0) return false;

    const dayOfWeek = timeValue.day();
    const hoursForDay = getHoursForDay(dayOfWeek);

    if (!hoursForDay || !hoursForDay.isOpen) return true;

    const hour = timeValue.hour();
    const minute = timeValue.minute();
    const currentTime = hour * 60 + minute; // Convert to minutes for easier comparison

    // Parse business hours
    const openTime1 = parseTime(hoursForDay.openTime);
    const closeTime1 = parseTime(hoursForDay.closeTime);
    const openTime2 = parseTime(hoursForDay.openTime2);
    const closeTime2 = parseTime(hoursForDay.closeTime2);

    if (view === 'hours') {
      // Check if the hour is within any valid period
      let isValidHour = false;

      // First period (e.g., lunch)
      if (openTime1 && closeTime1) {
        const open1Minutes = openTime1.hours * 60;
        const close1Minutes = closeTime1.hours * 60;
        if (hour >= openTime1.hours && hour <= closeTime1.hours) {
          isValidHour = true;
        }
      }

      // Second period (e.g., dinner)
      if (openTime2 && closeTime2) {
        if (hour >= openTime2.hours && hour <= closeTime2.hours) {
          isValidHour = true;
        }
      }

      return !isValidHour;
    }

    if (view === 'minutes') {
      // Check if the exact time is within any valid period
      let isValidTime = false;

      // First period
      if (openTime1 && closeTime1) {
        const open1Minutes = openTime1.hours * 60 + openTime1.minutes;
        const close1Minutes = closeTime1.hours * 60 + closeTime1.minutes;
        if (currentTime >= open1Minutes && currentTime <= close1Minutes) {
          isValidTime = true;
        }
      }

      // Second period
      if (openTime2 && closeTime2) {
        const open2Minutes = openTime2.hours * 60 + openTime2.minutes;
        const close2Minutes = closeTime2.hours * 60 + closeTime2.minutes;
        if (currentTime >= open2Minutes && currentTime <= close2Minutes) {
          isValidTime = true;
        }
      }

      return !isValidTime;
    }

    return false;
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <LocalizationProvider
          fullWidth
          dateAdapter={AdapterDayjs}
          adapterLocale="pt"
          localeText={localeText}
        >
          <DateTimePicker
            shouldDisableDate={shouldDisableDate}
            shouldDisableTime={shouldDisableTime}
            disablePast
            minDate={dayjs()}
            onChange={(newValue) => {
              if (!newValue) {
                setValue(name, null);
              } else {
                field.onChange(newValue?.isValid() ? newValue : null);
              }
            }}
            {...field}
            value={field.value || null}
            error={!!error}
            text={error?.message}
            fullWidth
            {...other}
          />
        </LocalizationProvider>
      )}
    />
  );
}

RHFDateTimePicker.propTypes = {
  helperText: PropTypes.string,
  name: PropTypes.string,
  type: PropTypes.string,
};
